<template lang="pug">
ValidationProvider.p-relative(
  v-slot="{ invalid, errors }"
  tag="div"
  vid="monthly-recurrence"
  rules="required"
  :model-value="monthlyOption"
)
  .form-group.m-0(:class="{ 'has-error': invalid || hasApiErrors }")
    .d-flex.flex-column.align-items-start.justify-content-between
      label.control-label(for="mpnthly-recurrence")
        span.mr-10 On
      AppSelect#monthly-recurrence(
        v-model="monthlyOption"
        :options="options"
        :disabled="isJobInThePast"
        :select2-options="select2Options"
        @input="onSelect"
      )
      .a-errors(
        v-show="invalid || hasApiErrors"
        :class="{ 'has-error': invalid || hasApiErrors }"
      )
        template(v-if="errors && errors.length")
          span.help-block(
            v-for="(err, index) in errors"
            :key="`${err}-${index}`"
          ) {{ err }}
        template(v-if="hasApiErrors")
          span.help-block(
            v-for="(err, index) in monthlyErrors"
            :key="`${err}-${index}`"
          ) {{ err }}
</template>

<script>
import { get, pick, isNil } from 'lodash-es';
import { formatInternalDate } from '~/utils/time_utils';
import { getMonthlyNthDayForDate } from '~/utils/recurring_schedule';

const ONDAY = 'onday';
const ONNTHDAY = 'onnthday';

export default {
  name: 'RecurringJobMonthlyInput',
  inject: ['isJobInThePast'],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    date: {
      type: Object, // Moment.js instance
      required: true,
    },
    scheduleDate: {
      type: Object, // Moment.js instance
    },
    select2Options: {
      type: Object,
      required: true,
    },
    schedule: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['input'],
  data: () => ({
    originalDate: null,
    monthlyOption: ONNTHDAY,
  }),
  computed: {
    monthlyApiErrors() {
      let apiErrors = [];

      if (this.monthlyOption === ONDAY) {
        apiErrors = get(this.errors, 'monthday', []);
      } else {
        const { monthweekday = [], monthweekdaynum = [] } = pick(this.errors);

        apiErrors = [...monthweekday, ...monthweekdaynum];
      }

      return apiErrors;
    },
    hasApiErrors() {
      return !!this.monthlyApiErrors.length;
    },
    monthlyNthOption() {
      let dateToCompute = this.editing ? this.scheduleDate : this.date;

      // If the user has changed the date, then we need to compute the options
      // based on the new selected date.
      if (
        this.editing &&
        this.originalDate &&
        formatInternalDate(this.date) !== formatInternalDate(this.originalDate)
      ) {
        dateToCompute = this.date;
      }

      return getMonthlyNthDayForDate(formatInternalDate(dateToCompute));
    },
    options() {
      const { text } = this.monthlyNthOption;

      return [
        { id: ONDAY, text: `The ${this.date.format('Do')}` },
        { id: ONNTHDAY, text: `The ${text}` },
      ];
    },
  },
  watch: {
    date() {
      this.onSelect(this.monthlyOption);
    },
  },
  async mounted() {
    this.originalDate = this.date.clone();

    if (!this.editing) {
      // Force `ONNTHDAY` by default when mounting
      // so we propagate the changes to the parent.
      this.onSelect(ONNTHDAY);
    } else {
      this.monthlyOption = !isNil(this.schedule.monthday) ? ONDAY : ONNTHDAY;

      this.onSelect(this.monthlyOption);
    }
  },
  methods: {
    onSelect(value) {
      const payload = {
        monthday: null,
        monthweekday: null,
        monthweekdaynum: null,
      };

      if (value === ONNTHDAY) {
        const {
          data: [monthweekday, monthweekdaynum],
        } = this.monthlyNthOption;

        payload.monthweekday = monthweekday;
        payload.monthweekdaynum = monthweekdaynum;
      } else {
        payload.monthday = this.date.date();
      }

      this.$emit('input', payload);
    },
  },
};
</script>
